import { Box, Button, Paper, Tooltip, Typography } from "@mui/material";
import { Edit as EditIcon, Lock as LockIcon } from "@mui/icons-material";
import type {
  ChangeRequestView,
  ContactEmails,
  EditFieldGovernance,
  OperationalFields,
  VendorProfileDocument,
  VendorProfileFacility,
  VendorProfileVendor,
} from "../types";
import { FieldEditRow } from "./FieldEditRow";
import type { EditTarget } from "./EditDrawer";
import { deriveCommercial } from "@/pages/VendorOnboarding/reviewer/commercialTerms";
import { findDocument } from "../utils/documentLookup";
import { maskAccountNumber, maskPan } from "../utils/masking";
import { buildEditTarget } from "./editTargetBuilders";
import { formatPhone } from "../utils/phone";
import { isRowLocked, lockTooltip } from "./governanceLabels";
import type { VendorCategory } from "@/services/vendorCategories";
import { isKwsInstallation, majorLabel, subLabel } from "../utils/vendorCategories";

type Props = {
  facilityId: string;
  facility: VendorProfileFacility;
  vendor: VendorProfileVendor | null | undefined;
  governance: Record<string, EditFieldGovernance>;
  contactEmails: ContactEmails;
  documents: VendorProfileDocument[];
  changeRequests: ChangeRequestView[];
  operational?: OperationalFields | null;
  isInternal?: boolean;
  categories?: VendorCategory[];
  onEdit: (target: EditTarget) => void;
};

interface RowSpec {
  label: string;
  fieldName: string;
  value: unknown;
  visible?: boolean;
  isAttachment?: boolean;
  attachment?: VendorProfileDocument;
}

interface Group {
  title: string;
  rows: RowSpec[];
  sectionEdit?: { anchorField: string; label: string };
}

export function ProfileEditsPanel({
  facilityId,
  facility,
  vendor,
  governance,
  contactEmails,
  documents,
  changeRequests,
  operational,
  isInternal = false,
  categories,
  onEdit,
}: Props) {
  // BE ships pan_status on the operational block per the /edit-view contract;
  // fall through to vendor.pan_status for backward compat with older responses.
  const panStatus =
    (operational?.pan_status as string | null | undefined) ??
    vendor?.pan_status ??
    null;
  const isKws = isKwsInstallation(facility);
  const emailList = (emails: string[] | undefined) =>
    emails && emails.length > 0 ? emails.join(", ") : "";
  const effectiveGovernance = governance;
  const pendingFields = new Set<string>(
    changeRequests
      .filter((cr) => cr.status === "PENDING")
      .flatMap((cr) => cr.fields ?? []),
  );
  const derived = deriveCommercial(
    facility.first_tranche_payment_basis ?? undefined,
    facility.payment_terms_pattern ?? undefined,
    facility.credit_days != null ? String(facility.credit_days) : undefined,
  );
  const msmeDoc = vendor
    ? findDocument(documents, "vendor", vendor.id, "MRC")
    : undefined;

  const groups: Group[] = [
    {
      title: "IDENTITY",
      rows: [
        { label: "Vendor Type", fieldName: "vendor_type", value: vendor?.type },
        { label: "Vendor Legal Name", fieldName: "legal_name", value: facility.legal_name },
        { label: "PAN Number", fieldName: "pan_number", value: maskPan(vendor?.pan_card) },
        { label: "GST Number", fieldName: "gst", value: facility.gst },
        {
          label: "MSME Certificate",
          fieldName: "msme_certificate",
          value: msmeDoc?.file ?? "",
          isAttachment: true,
          attachment: msmeDoc,
        },
      ],
    },
    {
      title: "CONTACT",
      rows: [
        { label: "Primary Email ID", fieldName: "email_id", value: facility.email_id },
        {
          label: "POC Number",
          fieldName: "poc_contact",
          value: formatPhone(facility.poc_contact, facility.poc_contact_country_code),
        },
        {
          label: "PO Notification Emails",
          fieldName: "po_notification_emails",
          value: emailList(contactEmails.po_notification_emails),
        },
        {
          label: "Escalation Emails",
          fieldName: "escalation_emails",
          value: emailList(contactEmails.escalation_emails),
        },
        {
          label: "Vendor Invoice Portal Access",
          fieldName: "vendor_invoice_portal_access_email",
          value: emailList(contactEmails.vendor_invoice_portal_access_email),
        },
        {
          label: "TARs Production Process Access",
          fieldName: "tars_production_process_management_access_email",
          value: emailList(
            contactEmails.tars_production_process_management_access_email,
          ),
          visible: isKws,
        },
      ],
    },
    {
      title: "ADDRESS",
      sectionEdit: { anchorField: "address_billing", label: "Address" },
      rows: [
        { label: "Billing Address", fieldName: "address_billing", value: facility.address_billing },
        { label: "Pincode", fieldName: "location_pincode", value: facility.location_pincode },
      ],
    },
    {
      title: "BANKING",
      sectionEdit: { anchorField: "account_number", label: "Bank Details" },
      rows: [
        { label: "Bank Account Number", fieldName: "account_number", value: maskAccountNumber(facility.account_number) },
        { label: "IFSC Code", fieldName: "ifsc_code", value: facility.ifsc_code },
      ],
    },
    {
      title: "VENDOR PROFILE TYPE",
      sectionEdit: { anchorField: "super_category", label: "Vendor Profile Type" },
      rows: [
        {
          label: "Super Category",
          fieldName: "super_category",
          value: majorLabel(categories, facility.super_category),
        },
        {
          label: "Sub Category",
          fieldName: "sub_category",
          value: subLabel(categories, facility.super_category, facility.sub_category),
        },
      ],
    },
    {
      title: "COMMERCIAL",
      rows: [
        { label: "Credit Days", fieldName: "credit_days", value: facility.credit_days },
        {
          label: "Payment Terms Pattern",
          fieldName: "payment_terms_pattern",
          value: facility.payment_terms_pattern,
        },
        {
          label: "Payment Terms Remarks",
          fieldName: "payment_terms_remarks",
          value: derived.remarks,
        },
      ],
    },
  ];

  const sectionState = (g: Group) => {
    const { anchorField, label } = g.sectionEdit!;
    const rows = g.rows.filter((r) => r.visible !== false);
    const anchor = rows.find((r) => r.fieldName === anchorField) ?? rows[0];
    const lockedRow = rows.find((r) =>
      isRowLocked(effectiveGovernance[r.fieldName], r.value, r.fieldName, isInternal, {
        panStatus,
      }),
    );
    const pendingLocked = rows.some((r) => pendingFields.has(r.fieldName));
    const tooltip = lockedRow
      ? lockTooltip(effectiveGovernance[lockedRow.fieldName], lockedRow.fieldName, {
          isInternal,
          panStatus,
        })
      : "A change request for this section is already pending — wait for it to be reviewed before editing again.";
    return { anchor, label, locked: Boolean(lockedRow) || pendingLocked, tooltip };
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {groups.map((g) => (
        <Box key={g.title}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              pr: 2,
              mb: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                letterSpacing: 0.8,
                color: "text.secondary",
              }}
            >
              {g.title}
            </Typography>
            {g.sectionEdit &&
              (() => {
                const section = sectionState(g);
                return section.locked ? (
                  <Tooltip title={section.tooltip}>
                    <Box
                      component="span"
                      tabIndex={0}
                      aria-label={`${section.label} is locked`}
                      sx={{
                        display: "inline-flex",
                        "&:focus-visible": {
                          outline: "2px solid",
                          outlineColor: "primary.main",
                          borderRadius: 0.5,
                        },
                      }}
                    >
                      <LockIcon sx={{ fontSize: 18, color: "text.disabled" }} />
                    </Box>
                  </Tooltip>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon sx={{ fontSize: 16 }} />}
                    aria-label={`Edit ${section.label}`}
                    onClick={() => {
                      const target = buildEditTarget(
                        section.anchor,
                        facilityId,
                        facility,
                        vendor,
                        documents,
                        effectiveGovernance,
                      );
                      if (target) onEdit({ ...target, title: `Edit · ${section.label}` });
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Edit</Typography>
                  </Button>
                );
              })()}
          </Box>
          <Paper variant="outlined" sx={{ borderColor: "#E0E4EF", borderRadius: 1 }}>
            {g.rows
              .filter((r) => r.visible !== false)
              .map((row) => (
                <FieldEditRow
                  key={row.fieldName}
                  readOnly={Boolean(g.sectionEdit)}
                  label={row.label}
                  fieldName={row.fieldName}
                  value={row.value}
                  governance={effectiveGovernance[row.fieldName]}
                  hasPendingChange={pendingFields.has(row.fieldName)}
                  isAttachment={row.isAttachment}
                  attachment={row.attachment}
                  isInternal={isInternal}
                  panStatus={panStatus}
                  onEdit={() => {
                    const target = buildEditTarget(
                      row,
                      facilityId,
                      facility,
                      vendor,
                      documents,
                      effectiveGovernance,
                    );
                    if (target) onEdit(target);
                  }}
                />
              ))}
          </Paper>
        </Box>
      ))}
    </Box>
  );
}

export default ProfileEditsPanel;
